import React from 'react'
import PropTypes from 'prop-types'
import { uniq } from 'lodash'
import { Card, CardHeader, CardTitle, CardContent } from '../../ui/card'
import { Progress } from '../../ui/progress'

function SkillsOverview({ skills }) {
  const calculateProgress = (skillsList) => {
    if (!skillsList || skillsList.length === 0) return 0
    const completed = skillsList.filter((s) => s.status === 'completed').length
    return Math.round((completed / skillsList.length) * 100)
  }

  const getProgressByCategory = () => {
    if (!skills || skills.length === 0) return []
    const categories = uniq(skills.map((s) => s.category))
    return categories.map((category) => {
      const categorySkills = skills.filter((s) => s.category === category)
      return {
        category,
        progress: calculateProgress(categorySkills),
        completed: categorySkills.filter((s) => s.status === 'completed').length,
        total: categorySkills.length
      }
    })
  }

  const overallProgress = calculateProgress(skills)
  const categoryProgress = getProgressByCategory()

  if (!skills || skills.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Skills Progress</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-center text-gray-500 py-8">No skills assigned yet.</p>
        </CardContent>
      </Card>
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Skills Progress</CardTitle>
        <p className="text-sm text-gray-600 mt-1">Progress in current class skills</p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-lg">
          <div className="text-6xl font-bold text-purple-600 mb-2">{overallProgress}%</div>
          <p className="text-lg text-gray-700 font-medium">Level Progress</p>
          <p className="text-sm text-gray-600 mt-1">
            {skills.filter((s) => s.status === 'completed').length} of {skills.length} skills mastered
          </p>
          {overallProgress === 100 && (
            <p className="text-sm font-semibold text-green-600 mt-2">🎉 Ready for next level!</p>
          )}
        </div>

        <div className="space-y-4">
          <h3 className="text-lg font-semibold text-gray-900">Progress by Category</h3>
          {categoryProgress.map(({ category, progress, completed, total }) => (
            <div key={category ?? 'all-skills'} className="space-y-2">
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium text-gray-700">{category ?? 'All Skills'}</span>
                <span className="text-sm text-gray-500">
                  {completed}/{total}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <Progress value={progress} className="flex-1" />
                <span className="text-sm font-semibold text-purple-600 w-12 text-right">{progress}%</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}

SkillsOverview.propTypes = {
  skills: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      category: PropTypes.string,
      status: PropTypes.string.isRequired
    })
  ).isRequired
}

export default SkillsOverview
